import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowRight,
  FaChevronRight,
  FaEye,
  FaGripVertical,
  FaIndustry,
  FaSave,
  FaSearch,
} from "react-icons/fa";
import b2bHubHomeService from "../../services/b2bHubHome.service";
import {
  fetchHubHomeSections,
  hubHomeSectionExtractMessage,
  updateHubHomeSection,
} from "../../services/hubHomeSectionAdmin.service";
import {
  buildTopIndustriesSectionConfig,
  HUB_HOME_SECTION_KEYS,
  resolveSectionGridConfig,
  resolveTopIndustrySlugs,
} from "../../constants/hubHomeSections";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { CM_INDUSTRY_IMAGE_BY_SLUG } from "../../constants/industryTypeMap";
import "../../assets/styles/master_category_admin.scss";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/super_admin_service_quick_links.scss";
import "../../assets/styles/super_admin_trading_categories.scss";

const TOP_INDUSTRIES_PAGE_SIZE = 10;

function extractHomePayload(response) {
  const envelope = response?.data ?? response ?? {};
  return envelope?.data ?? envelope;
}

function mapIndustry(row) {
  const slug = String(row.slug ?? "").trim();
  return {
    slug,
    name: String(row.name ?? "").trim(),
    image_path: row.image_path ?? null,
    count: Number(row.count ?? 0),
    product_count: Number(row.product_count ?? 0),
    rfq_count: Number(row.rfq_count ?? 0),
    verified_count: Number(row.verified_count ?? 0),
  };
}

const formatStatCount = (value) => (Number(value) || 0).toLocaleString();

const industryActivityScore = (industry) =>
  Number(industry?.count ?? 0) +
  Number(industry?.product_count ?? 0) +
  Number(industry?.rfq_count ?? 0);

const compareIndustriesByActivity = (a, b) => {
  const scoreDiff = industryActivityScore(b) - industryActivityScore(a);
  if (scoreDiff !== 0) return scoreDiff;

  const manufacturerDiff = Number(b.count ?? 0) - Number(a.count ?? 0);
  if (manufacturerDiff !== 0) return manufacturerDiff;

  const productDiff = Number(b.product_count ?? 0) - Number(a.product_count ?? 0);
  if (productDiff !== 0) return productDiff;

  const rfqDiff = Number(b.rfq_count ?? 0) - Number(a.rfq_count ?? 0);
  if (rfqDiff !== 0) return rfqDiff;

  return a.name.localeCompare(b.name);
};

const sortIndustriesByActivity = (industries) => [...industries].sort(compareIndustriesByActivity);

function IndustryStats({ item, compact = false }) {
  return (
    <div className={`sa-tc-stats${compact ? " sa-tc-stats--compact" : ""}`}>
      <span className="sa-tc-stats__item">
        <strong>{formatStatCount(item.count)}</strong> Manufacturers
      </span>
      <span className="sa-tc-stats__item">
        <strong>{formatStatCount(item.product_count)}</strong> Products
      </span>
      <span className="sa-tc-stats__item">
        <strong>{formatStatCount(item.rfq_count)}</strong> RFQs
      </span>
    </div>
  );
}

function ToggleSwitch({ checked, disabled, onChange, label }) {
  return (
    <label className="sa-toggle" title={label}>
      <input
        type="checkbox"
        className="sa-toggle__input"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="sa-toggle__track" aria-hidden />
    </label>
  );
}

export default function SuperAdminTopIndustriesPage() {
  const [section, setSection] = useState(null);
  const [allIndustries, setAllIndustries] = useState([]);
  const [selectedSlugs, setSelectedSlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(TOP_INDUSTRIES_PAGE_SIZE);

  const industryBySlug = useMemo(
    () => Object.fromEntries(allIndustries.map((industry) => [industry.slug, industry])),
    [allIndustries],
  );

  const displayConfig = useMemo(() => {
    if (!section) {
      return resolveSectionGridConfig(HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES, null);
    }
    return resolveSectionGridConfig(
      HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES,
      section.sectionConfig ?? section.section_config,
    );
  }, [section]);

  const maxIndustries = displayConfig.total_items;
  const isManualMode = selectedSlugs.length > 0;
  const atIndustryLimit = selectedSlugs.length >= maxIndustries;

  const orderedIndustryOptions = useMemo(() => sortIndustriesByActivity(allIndustries), [allIndustries]);

  const filteredIndustryOptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return orderedIndustryOptions;
    return orderedIndustryOptions.filter((industry) => industry.name.toLowerCase().includes(query));
  }, [orderedIndustryOptions, searchQuery]);

  const visibleIndustryOptions = useMemo(
    () => filteredIndustryOptions.slice(0, visibleCount),
    [filteredIndustryOptions, visibleCount],
  );

  const hasMoreIndustries = visibleCount < filteredIndustryOptions.length;
  const nextBatchSize = Math.min(TOP_INDUSTRIES_PAGE_SIZE, filteredIndustryOptions.length - visibleCount);

  const selectedIndustriesByActivity = useMemo(
    () =>
      sortIndustriesByActivity(
        selectedSlugs.map((slug) => industryBySlug[slug]).filter(Boolean),
      ),
    [selectedSlugs, industryBySlug],
  );

  const buildActivityOrderedSlugs = useCallback(
    (slugs) =>
      sortIndustriesByActivity(slugs.map((slug) => industryBySlug[slug]).filter(Boolean)).map(
        (industry) => industry.slug,
      ),
    [industryBySlug],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sections, homeResponse] = await Promise.all([
        fetchHubHomeSections(),
        b2bHubHomeService.getHome(),
      ]);

      const topSection = sections.find(
        (row) => (row.sectionKey || row.section_key) === HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES,
      );
      const homeData = extractHomePayload(homeResponse);
      const sectionConfig = topSection?.sectionConfig ?? topSection?.section_config;
      const grid = resolveSectionGridConfig(HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES, sectionConfig);
      const industryRows = Array.isArray(homeData?.contract_manufacturer_industries)
        ? homeData.contract_manufacturer_industries
        : [];

      setSection(topSection || null);
      setAllIndustries(
        sortIndustriesByActivity(
          industryRows.map(mapIndustry).filter((row) => row.slug && row.name),
        ),
      );
      setSelectedSlugs(resolveTopIndustrySlugs(sectionConfig, grid.total_items));
      setVisibleCount(TOP_INDUSTRIES_PAGE_SIZE);
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not load Top Industries settings."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setVisibleCount(TOP_INDUSTRIES_PAGE_SIZE);
  }, [searchQuery]);

  const toggleIndustry = (slug, checked) => {
    if (!slug) return;

    setSelectedSlugs((prev) => {
      if (checked) {
        if (prev.includes(slug) || prev.length >= maxIndustries) return prev;
        return [...prev, slug];
      }
      return prev.filter((item) => item !== slug);
    });
  };

  const saveIndustries = async () => {
    if (!section?.id) return;

    if (selectedSlugs.length > maxIndustries) {
      toast.error(`Select at most ${maxIndustries} industries for the current display layout.`);
      return;
    }

    setSaving(true);
    try {
      const orderedSlugs = buildActivityOrderedSlugs(selectedSlugs).slice(0, maxIndustries);
      const updated = await updateHubHomeSection(section.id, {
        section_config: buildTopIndustriesSectionConfig(
          section.sectionConfig ?? section.section_config,
          orderedSlugs,
        ),
      });
      if (updated) {
        setSection(updated);
        const grid = resolveSectionGridConfig(
          HUB_HOME_SECTION_KEYS.TOP_INDUSTRIES,
          updated.sectionConfig ?? updated.section_config,
        );
        setSelectedSlugs(
          resolveTopIndustrySlugs(updated.sectionConfig ?? updated.section_config, grid.total_items),
        );
        toast.success("Top Industries updated.");
      }
    } catch (err) {
      toast.error(hubHomeSectionExtractMessage(err, "Could not update Top Industries."));
      await load();
    } finally {
      setSaving(false);
    }
  };

  const resolveIndustryImage = (industry) => {
    const imagePath = industry.image_path || CM_INDUSTRY_IMAGE_BY_SLUG[industry.slug];
    return imagePath ? resolvePublicFileUrl(imagePath) : null;
  };

  return (
    <div className="mca-page super-admin-page sa-sql-page sa-tc-page">
      <nav className="mca-breadcrumb" aria-label="Breadcrumb">
        <ol className="mca-breadcrumb__list">
          <li>
            <Link to="/super-admin/dashboard">Super Admin</Link>
          </li>
          <li>
            <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>
          </li>
          <li aria-current="page">Top Industries</li>
        </ol>
      </nav>

      <div className="sa-sql-page-head">
        <header className="sa-sql-hero">
          <div className="sa-sql-hero__title-row">
            <span className="sa-sql-hero__icon" aria-hidden>
              <FaIndustry />
            </span>
            <h1 className="sa-sql-hero__title">Top Industries</h1>
            <span className="sa-sql-hero__badge">Super Admin</span>
          </div>
          <p className="sa-sql-hero__subtitle">
            Choose which contract manufacturing industries appear in the Top Industries section on the home page.
            Industries are sorted by highest activity (manufacturers + products + RFQs). You can select up to{" "}
            <strong>{maxIndustries}</strong> industries ({displayConfig.rows} rows × {displayConfig.products_per_row}{" "}
            per row) from{" "}
            <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
          </p>
        </header>
        <div className="sa-sql-page-head__actions">
          <Link
            to="/home"
            className="mca-btn mca-btn--outline sa-sql-preview-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaEye aria-hidden /> Preview on Home Page
          </Link>
          <button
            type="button"
            className="mca-btn mca-btn--primary sa-sql-save-btn"
            onClick={() => void saveIndustries()}
            disabled={loading || saving || !section}
          >
            <FaSave aria-hidden /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="sa-placeholder-copy">Loading industries…</p>
      ) : !section ? (
        <p className="sa-placeholder-copy">
          Top Industries section is missing. Restore it from{" "}
          <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
        </p>
      ) : (
        <>
          {!isManualMode ? (
            <div className="sa-tc-notice">
              No manual selection saved yet. The home page currently uses automatic trending industries (sorted by
              manufacturer count). Select industries below and save to override.
            </div>
          ) : null}

          <section className="sa-sql-order-card">
            <h2 className="sa-sql-order-card__title">Home page order (highest activity first)</h2>
            {selectedIndustriesByActivity.length ? (
              <div className="sa-sql-order-flow">
                {selectedIndustriesByActivity.map((industry, index) => (
                  <React.Fragment key={industry.slug}>
                    <div className="sa-sql-order-flow__item">
                      <span className="sa-sql-order-flow__num">{index + 1}</span>
                      <span className="sa-sql-order-flow__label">{industry.name}</span>
                    </div>
                    {index < selectedIndustriesByActivity.length - 1 ? (
                      <FaArrowRight className="sa-sql-order-flow__arrow" aria-hidden />
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <p className="sa-tc-empty-order">
                Automatic trending industries are shown on the home page (sorted by manufacturer count).
              </p>
            )}
          </section>

          <section className="sa-sql-card">
            <div className="sa-sql-card__head sa-tc-card__head">
              <h2 className="sa-sql-card__title">
                CM industries ({selectedSlugs.length} of {maxIndustries} selected) — showing{" "}
                {visibleIndustryOptions.length} of {filteredIndustryOptions.length}
                {searchQuery.trim() ? " matching" : ""}, sorted by activity
              </h2>
              <div className="mca-search sa-tc-search">
                <FaSearch className="mca-search__icon" aria-hidden />
                <input
                  type="search"
                  className="mca-input mca-search__input"
                  placeholder="Search industries…"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  aria-label="Search contract manufacturing industries"
                />
              </div>
            </div>

            {filteredIndustryOptions.length ? (
              <ul className="sa-sql-tile-list sa-tc-category-list">
                {visibleIndustryOptions.map((industry, activityIndex) => {
                  const isOn = selectedSlugs.includes(industry.slug);
                  const imageSrc = resolveIndustryImage(industry);

                  return (
                    <li
                      key={industry.slug}
                      className={`sa-tc-category-list__item${isOn ? "" : " sa-tc-category-list__item--off"}`}
                    >
                      <div className="sa-tc-category-list__main">
                        <span className="sa-sql-tile-list__grip" aria-hidden>
                          <FaGripVertical />
                        </span>
                        <span className={`sa-sql-tile-list__order${isOn ? "" : " sa-sql-tile-list__order--off"}`}>
                          {activityIndex + 1}
                        </span>
                        <span className="sa-tc-category-list__thumb">
                          {imageSrc ? <img src={imageSrc} alt="" /> : <FaIndustry aria-hidden />}
                        </span>
                        <div className="sa-sql-tile-list__copy">
                          <span className="sa-sql-tile-list__title">{industry.name}</span>
                          <IndustryStats item={industry} />
                          <span className="sa-sql-tile-list__desc">
                            {isOn ? "Shown on home page" : "Hidden from home page"}
                          </span>
                        </div>
                        <div className="sa-sql-tile-list__controls">
                          <ToggleSwitch
                            checked={isOn}
                            disabled={saving || (!isOn && atIndustryLimit)}
                            onChange={(checked) => toggleIndustry(industry.slug, checked)}
                            label={`${isOn ? "Hide" : "Show"} ${industry.name} on home`}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="sa-tc-search-empty">
                No industries match &ldquo;{searchQuery.trim()}&rdquo;. Try a different search term.
              </p>
            )}

            {hasMoreIndustries ? (
              <div className="sa-tc-load-more">
                <button
                  type="button"
                  className="sa-tc-load-more__btn"
                  onClick={() => setVisibleCount((count) => count + TOP_INDUSTRIES_PAGE_SIZE)}
                >
                  View More
                  <span className="sa-tc-load-more__count">
                    ({nextBatchSize} more {nextBatchSize === 1 ? "industry" : "industries"})
                  </span>
                  <FaChevronRight aria-hidden />
                </button>
              </div>
            ) : null}
          </section>

          <section className="sa-sql-info-grid">
            <div className="sa-sql-info-card">
              <h3>Activity Ranking</h3>
              <p>
                Industries are listed highest to lowest by manufacturers + products + RFQs. Selected industries appear on
                the home page in the same activity order.
              </p>
            </div>
            <div className="sa-sql-info-card">
              <h3>Industry Images</h3>
              <p>
                Images come from{" "}
                <Link to="/super-admin/master-data/cm-industry-type-management">CM Industry Type Management</Link> or
                bundled defaults.
              </p>
            </div>
            <div className="sa-sql-info-card">
              <h3>Grid Layout</h3>
              <p>
                Rows and industries-per-row are configured from{" "}
                <Link to="/super-admin/master-data/home-section-management">Home Section Management</Link>.
              </p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
